/**
 * The Settings → Bot Detection funnel: how many clicks, conversions, sales and
 * how much revenue each bot rule removes, rule by rule in order, from one
 * period's get_bot_funnel rows (clicks_daily summed per bot bits, migration
 * 20261022100000).
 *
 * Computed here from the rules being edited, so switching a rule moves the
 * funnel before it is saved. The same logic runs in SQL as bot_verdict_sql -
 * what Clicks counts as Bots Oracul - and the tests pin the two together.
 */

export interface BotRule {
  key: string;
  /** The rule's bit in clicks_daily.bot_mask. */
  bit: number;
  label: string;
  description: string;
  /** User-agent / ISP strings. */
  patterns: string[];
  /** Whether `patterns` is edited from the UI. */
  editable: boolean;
  enabled: boolean;
  sort: number;
  updated_at?: string;
}

export interface BotFunnelRow {
  /** null: a day without bot bits, not classified. */
  mask: number | null;
  /** The server's verdict under the saved rules. */
  is_bot: boolean | null;
  /** The day was refreshed before bot bits existed (20261019170000): no rule sees it until recomputed. */
  legacy?: boolean;
  clicks: number;
  unique_global: number;
  conversions: number;
  sales: number;
  revenue: number;
}

export interface Totals {
  clicks: number;
  uniqueGlobal: number;
  conversions: number;
  sales: number;
  revenue: number;
}

/** One row of the staircase: every filter rule, on or off, in its order. */
export interface FunnelStage {
  rule: BotRule;
  /** Clicks reaching this rule: what the rules before it left. */
  before: Totals;
  /** On: what this rule removes that no rule before it did. Off: nothing. */
  cut: Totals;
  /** Left after this rule (= `before` when it is off). */
  after: Totals;
  /**
   * On: what only this rule catches - no other rule that is on does, so it is
   * what switching it off would give back. Off: what switching it on would
   * remove from the clean traffic.
   */
  only: Totals;
}

export interface BotFunnel {
  total: Totals;
  stages: FunnelStage[];
  /** After every rule that is on: what Clicks shows with bots hidden. */
  clean: Totals;
  /** Caught by two or more rules that are on: no single switch gives it back. */
  overlap: Totals;
  /** Never classified (mask null): kept, as "hide bots" keeps them. */
  unclassified: Totals;
  /** The part of `unclassified` from days refreshed before bot bits existed. */
  legacy: Totals;
}

export const ZERO_TOTALS: Totals = { clicks: 0, uniqueGlobal: 0, conversions: 0, sales: 0, revenue: 0 };

export const sumRows = (rows: BotFunnelRow[]): Totals =>
  rows.reduce<Totals>(
    (t, r) => ({
      clicks: t.clicks + r.clicks,
      uniqueGlobal: t.uniqueGlobal + r.unique_global,
      conversions: t.conversions + r.conversions,
      sales: t.sales + r.sales,
      revenue: t.revenue + r.revenue,
    }),
    ZERO_TOTALS,
  );

export const ruleMatches = (rule: BotRule, row: BotFunnelRow): boolean =>
  row.mask !== null && (row.mask & rule.bit) !== 0;

const combine = (a: Totals, b: Totals, sign: 1 | -1): Totals => ({
  clicks: a.clicks + sign * b.clicks,
  uniqueGlobal: a.uniqueGlobal + sign * b.uniqueGlobal,
  conversions: a.conversions + sign * b.conversions,
  sales: a.sales + sign * b.sales,
  revenue: a.revenue + sign * b.revenue,
});

export function botFunnel(rows: BotFunnelRow[], rules: BotRule[]): BotFunnel {
  const filters = [...rules].sort((a, b) => a.sort - b.sort);
  const unclassified = rows.filter((r) => r.mask === null);
  const candidates = rows.filter((r) => r.mask !== null);
  const enabled = filters.filter((r) => r.enabled);
  // No rule removes these; they are in every stage's before/after.
  const neverRemoved = sumRows(unclassified);

  let left = candidates;
  const cuts = new Map<string, Totals>();
  const befores = new Map<string, Totals>();
  for (const rule of filters) {
    befores.set(rule.key, combine(sumRows(left), neverRemoved, 1));
    if (!rule.enabled) continue;
    cuts.set(rule.key, sumRows(left.filter((r) => ruleMatches(rule, r))));
    left = left.filter((r) => !ruleMatches(rule, r));
  }
  const clean = combine(sumRows(left), neverRemoved, 1);
  const hits = (r: BotFunnelRow) => enabled.filter((f) => ruleMatches(f, r)).length;

  const stages = filters.map((rule): FunnelStage => {
    const before = befores.get(rule.key)!;
    const cut = cuts.get(rule.key) ?? ZERO_TOTALS;
    const only = rule.enabled
      ? sumRows(candidates.filter((r) => ruleMatches(rule, r) && hits(r) === 1))
      : sumRows(left.filter((r) => ruleMatches(rule, r)));
    return { rule, before, cut, only, after: combine(before, cut, -1) };
  });

  return {
    total: sumRows(rows),
    stages,
    clean,
    overlap: sumRows(candidates.filter((r) => hits(r) >= 2)),
    unclassified: sumRows(unclassified),
    legacy: sumRows(rows.filter((r) => r.legacy)),
  };
}

/** CR as the grid shows it: conversions per unique visitor (20261008100000). */
export const conversionRate = (t: Totals): number | null =>
  t.uniqueGlobal > 0 ? (t.conversions / t.uniqueGlobal) * 100 : null;

/**
 * A list field's text → patterns: one per line, trimmed, blanks and repeats
 * dropped. Never split on commas: ISP names carry them ("Amazon.com, Inc."),
 * and "Inc." alone would match nearly every provider.
 */
export const parsePatterns = (text: string): string[] => [
  ...new Set(
    text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean),
  ),
];

/** The same entries, whatever their order or repeats - all a list means to matching. */
export const samePatterns = (a: string[], b: string[]): boolean => {
  const sa = new Set(a), sb = new Set(b);
  return sa.size === sb.size && [...sa].every((p) => sb.has(p));
};

/**
 * Whether the draft of a rule differs from the saved rule: switched, or its
 * list holds other entries. Drives the "changed, not saved" dot, the change
 * bar and what Save writes. No saved rule (still loading): not changed.
 */
export const ruleChanged = (
  draft: Pick<BotRule, "enabled" | "patterns">,
  saved: Pick<BotRule, "enabled" | "patterns"> | undefined,
): boolean => !!saved && (draft.enabled !== saved.enabled || !samePatterns(draft.patterns, saved.patterns));
